/**
 * The caret's state, and the mechanics of putting one event at it.
 *
 * Note entry has two halves. This is the lower one: what the caret is, what the next note
 * will be, and the three operations every kind of note-writing shares -- refusing in words,
 * writing an event over what stands at the caret, and tying the one behind into it. The
 * upper half, which decides what a keystroke means, is {@link ./entry}.
 *
 * They are apart because the letters, the frets and whatever comes next all write notes the
 * same way and only differ in how they arrive at one. Keeping the writing here means each
 * of those can be read on its own page without the others.
 */

import type { Command } from "../commands/types";
import type { Duration } from "../document/duration";
import { plain } from "../document/duration";
import type { EventId } from "../document/ids";
import type { Step } from "../document/pitch";
import { locateEvent, occupiedTicks } from "../document/query";
import type { MusicEvent, NoteEvent, Score } from "../document/types";
import type { Caret } from "./entry-place";
import { advanced, placeOf, rewrite } from "./entry-place";
import { insertGrace } from "./entry-grace";
import { overwrite } from "./entry-voice";

/** The caret, and everything the next keystroke needs to know to become a note. */
export interface EntryState {
  readonly caret: Caret | null;
  readonly duration: Duration;
  /** The octave a bare letter is spelled in: the register the last note was written in. */
  readonly octave: number;
  /** The letter last written, so the next one can be put in the nearest octave to it. */
  readonly step: Step | null;
  /** The event last written, which a chord note or a tie attaches to. */
  readonly last: EventId | null;
  /** The string a typed fret lands on, on a tab staff. String 1 is the highest. */
  readonly string: number;
  /**
   * The note a run of fret digits is building, on a tab staff.
   *
   * A fret above nine is typed as two digits, and this is what says the second one belongs
   * to the first: see {@link ./entry-fret}. Anything but another digit clears it.
   */
  readonly fretting: EventId | null;
  /** Tie the next note back into the last one. */
  readonly tie: boolean;
  /**
   * Write the next note as a grace of this kind rather than as a note of its own.
   *
   * A mode rather than a one-off, because graces come in runs and because it is the mode a
   * notation editor offers: every note typed while it is on leans on the note the caret
   * stands at, and the caret does not move, so a second one joins the run behind the first.
   * Rests are unaffected -- a silence that takes no time is nothing at all.
   */
  readonly grace: NoteEvent["grace"];
  /**
   * Whether a typed letter names the pitch that is heard rather than the one that is stored.
   *
   * True while a concert-pitch score is on the screen: what a user types is what they are
   * looking at, so a `C` typed into a clarinet part there is stored as the written D that
   * sounds it. Off everywhere else, including on a fretted staff -- see `entry-spell`.
   */
  readonly concert: boolean;
}

/** Nothing selected, crotchets, middle register: the state an editor opens in. */
export const IDLE: EntryState = {
  caret: null,
  duration: plain("quarter"),
  octave: 4,
  step: null,
  last: null,
  string: 1,
  fretting: null,
  tie: false,
  concert: false,
  grace: null,
};

/** What follows from one input: the state to keep, and the edits to apply. */
export interface EntryResult {
  readonly state: EntryState;
  readonly commands: readonly Command[];
  /** Why nothing happened, in words fit to show, or `null` when something did. */
  readonly refusal: string | null;
}

export function refuse(state: EntryState, reason: string): EntryResult {
  return { state, commands: [], refusal: reason };
}

/**
 * Write one event at the caret and step past it.
 *
 * In grace mode a note is written as a grace instead: it is slipped in before the note the
 * caret stands at rather than written over it, and the caret stays where it is, because the
 * note a grace leans on is the one still to come. Every path that writes a note comes
 * through here -- typed letters and typed frets alike -- so the mode needs stating once.
 */
export function write(score: Score, state: EntryState, event: MusicEvent): EntryResult {
  const caret = state.caret;
  if (caret === null) return refuse(state, "there is nowhere to write: click a bar first");
  const place = placeOf(score, caret);
  if (typeof place === "string") return refuse(state, place);

  const grace = state.grace !== null && event.kind === "note";
  const written = grace
    ? insertGrace(place.voice, caret.onset, { ...event, grace: state.grace }, place.capacity)
    : overwrite(place.voice, caret.onset, event, place.capacity);
  if (typeof written === "string") return refuse(state, written);

  return {
    state: {
      ...state,
      caret: grace
        ? caret
        : advanced(score, caret, occupiedTicks(event, place.capacity), place.capacity),
      last: event.id,
      tie: false,
    },
    commands: [rewrite(caret, place, written.events)],
    refusal: null,
  };
}

/**
 * Start the tie on the note behind, so that its head of the same pitch leads into the new
 * one, or `null` when that note is not there to be tied from.
 *
 * This comes after the write in the batch rather than before it. A tie into the next bar
 * leaves the two notes in different bars and the order would not matter; a tie within one
 * bar would be thrown away by the write, which replaces the bar it lands in.
 */
export function tieBack(score: Score, last: EventId, step: Step, octave: number): Command | null {
  const at = locateEvent(score, last);
  if (at === null || at.event.kind !== "note") return null;
  const pitches = at.event.pitches.map((pitch) =>
    pitch.step === step && pitch.octave === octave ? { ...pitch, tieStart: true } : pitch,
  );
  return { kind: "setPitches", eventId: last, pitches };
}
